// 10A · Detalle del partido: marcador oscuro (.dt-score + .qrow), card de
// señal (.sigd sin stake: Pick / Ventana / Resultado, o estado actual con
// Tendencia / Umbral / Falta), gráfico edge + línea (EdgeChart, misma
// serie pick.edge_history que el modal del dashboard), cronología y CTA
// fijo "Seguir este partido · alerta al cerrar". Sin tab bar.
// Fuentes: useLiveV3 (/live_games + /picks + /active_signals),
// signals_history, /radar_vip_results. Partido cerrado: ficha con el
// registro histórico (la serie live no existe, igual que en /dashboard).
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useClientStats from '../../hooks/useClientStats';
import useRadarResults from '../../../hooks/useRadarResults';
import { matchOf, pickOf } from '../../components/SignalCard';
import EdgeChart, { useChartModel } from '../EdgeChart';
import { Nav, useToast, SigSkeleton } from '../ui';
import { ventanaDe } from '../hooks';
import { useLiveV3, pickDe, esHandballDe, fmtNum, pickLive, umbralDe, STT_CLS, STT_TXT, useFollow, gidDeSenal } from '../live';

const ShareIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M16 6l-4-4-4 4M12 2v13" /></svg>;
const abrev = (n) => String(n || '').replace(/[^A-Za-z0-9 ]/g, '').split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 3).toUpperCase() || '—';
const tLabel = (t, esHb, qLen) => {
  if (esHb) return `${t <= 30 ? '1T' : '2T'} ${Math.round(t)}'`;
  const q = Math.floor(t / qLen) + 1; return `${q <= 4 ? `Q${q}` : `OT${q - 4}`} ${Math.round(t - (q - 1) * qLen)}'`;
};

export default function Detalle() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { live, loading, picks } = useLiveV3();
  const { signals } = useClientStats();
  const { alerts } = useRadarResults();
  const { on: siguiendo, toggle } = useFollow(String(id).replace(/_\d{10,}$/, ''));

  // El id puede ser el del partido (aps_123 / hb_123) o el de un registro de
  // signals_history (aps_123_1789…): se resuelve al gid del partido.
  const cleanId = String(id).replace(/^aps_/, '');
  const sig = useMemo(() => state?.signal || signals.find((s) => s.id === id || gidDeSenal(s) === String(id) || gidDeSenal(s) === `aps_${cleanId}`) || null, [state, signals, id, cleanId]);
  const gid = sig ? gidDeSenal(sig) : String(id);
  const gclean = gid.replace(/^aps_/, '');
  const l = live.find((x) => x.gid === gid || x.gid === `aps_${gclean}`) || null;
  const pick = l?.pick || pickDe(picks, gid);
  const radar = useMemo(() => alerts.filter((a) => a.gid === gid || a.gid === `aps_${gclean}`).sort((a, b) => b.ts - a.ts), [alerts, gid, gclean]);
  const esHb = l ? l.esHb : esHandballDe(pick) || esHandballDe(sig || {});
  const model = useChartModel(pick, esHb);

  if (loading && !l && !sig) return <div className="v3"><div className="scroll nt safe-top dt"><Nav title="Detalle del partido" /><SigSkeleton /><SigSkeleton /></div></div>;
  if (!l && !pick && !sig && !radar.length) {
    return (
      <div className="v3"><div className="scroll nt safe-top dt"><Nav title="Detalle del partido" />
        <div className="empty"><h3>No encontramos este partido</h3><p>Puede que ya haya cerrado y no quede su ficha en vivo.</p><button type="button" className="cta ghost" onClick={() => navigate('/app')}>Volver al inicio</button></div>
      </div></div>
    );
  }

  // ── datos de cabecera ──
  const nombres = l ? { away: l.away, home: l.home } : sig ? { away: matchOf(sig).split(' vs ')[0], home: matchOf(sig).split(' vs ')[1] || '' }
    : radar.length ? { away: radar[0].partido.split(' @ ')[0], home: radar[0].partido.split(' @ ')[1] || '' } : { away: pick?.away_team || '?', home: pick?.home_team || '?' };
  const liga = l?.liga || sig?.league || sig?.liga || radar[0]?.liga || pick?.liga || '—';
  const m = l ? l.marcador : sig && sig.final_away != null && sig.final_home != null
    ? { away: Number(sig.final_away), home: Number(sig.final_home), total: Number(sig.final_away) + Number(sig.final_home) } : { away: null, home: null };
  const enVivo = !!l;
  const ritmo = esHb ? pick?.ritmo_obs : pick?.pace;
  const qtr = Array.isArray(pick?.qtr_detail) ? pick.qtr_detail : [];
  const mit = pick?.mitades || null;
  const compartir = async () => {
    const data = { title: `${nombres.away} vs ${nombres.home} · Edge Elite`, text: `${liga} · ${nombres.away} vs ${nombres.home}`, url: window.location.href };
    try { if (navigator.share) await navigator.share(data); else { await navigator.clipboard.writeText(data.url); toast('Enlace copiado', { ok: true, ms: 2000 }); } } catch (_e) { /* cancelado */ }
  };

  // ── card de señal ──
  const edgeSig = sig ? parseFloat(sig.edge_initial ?? sig.edge) : null;
  const resSig = sig ? (sig.result === 'WIN' ? { v: 'WIN', cls: 'g' } : sig.result === 'LOSS' ? { v: 'LOSS', cls: 'neg' } : sig.result === 'PUSH' ? { v: 'PUSH' } : { v: 'En juego' }) : null;
  const why = l && l.proyeccion != null && l.linea != null
    ? <>Proyección del motor <b>{esHb ? l.proyeccion.toFixed(1) : Math.round(l.proyeccion)} {esHb ? 'goles' : 'puntos'}</b> vs línea del mercado <b>{l.linea}</b>.{l.razon ? ` ${l.razon}` : ''}</>
    : sig && sig.total_final != null ? <>Cerró con total <b>{sig.total_final}</b> sobre línea <b>{sig.line}</b>.</> : null;

  // ── cronología (desc) ──
  const eventos = [];
  if (model) {
    const qLen = model.qLen;
    const h = model.pts;
    eventos.push({ t: h[0].t, k: `p0`, txt: <>Partido en análisis.{typeof h[0].l === 'number' ? <> Línea de apertura <b>{h[0].l}</b>.</> : null}</> });
    const umbP = esHb ? 3 : 10, umbN = esHb ? -2 : -10;
    const cruce = h.find((p) => p.e >= umbP || p.e <= umbN);
    if (cruce) eventos.push({ t: cruce.t, k: 'umb', txt: <><span className="stt w"><i />Vigilar</span>Edge <b>{fmtNum(cruce.e)}</b> alcanzó el umbral {fmtNum(cruce.e >= umbP ? umbP : umbN)}.</> });
    let cambios = 0;
    for (let i = h.length - 1; i > 0 && cambios < 3; i--) {
      if (typeof h[i].l === 'number' && typeof h[i - 1].l === 'number' && h[i].l !== h[i - 1].l) { eventos.push({ t: h[i].t, k: `l${i}`, txt: <>Línea pasó de <b>{h[i - 1].l}</b> a <b>{h[i].l}</b>.</> }); cambios++; }
    }
    eventos.forEach((e) => { e.lbl = tLabel(e.t, esHb, qLen); });
  }
  if (sig) eventos.push({ t: 1e6, k: 'sig', lbl: ventanaDe(sig).split(' · ')[0], txt: <><span className="stt g"><i />Entrar</span>Edge cruzó el umbral. Señal emitida: <b>{pickOf(sig)}</b>.{resSig && resSig.v !== 'En juego' ? <> Cerró <b>{resSig.v}</b>.</> : null}</> });
  radar.forEach((a, i) => eventos.push({ t: 1e6 + 1 + i, k: `r${a.id}`, lbl: a.ventana, txt: <><b>📡 Radar</b> {a.side} {a.linea ?? ''} con edge <b>{fmtNum(a.edge)}</b>.{a.resolved ? <> Cerró <b>{a.won ? 'WIN' : 'LOSS'}</b>{a.total_final != null ? <> · total {a.total_final}</> : null}.</> : null}</> }));
  eventos.sort((a, b) => b.t - a.t || (a.k === 'p0' ? 1 : b.k === 'p0' ? -1 : 0));

  const qn = esHb ? 2 : Math.max(4, qtr.length);
  const partidoTxt = `${nombres.away} vs ${nombres.home}`;

  return (
    <div className="v3"><div className="scroll nt safe-top dt">
      <Nav title="Detalle del partido" right={<button type="button" aria-label="Compartir" onClick={compartir} style={{ background: 'none', border: 'none', padding: 0, display: 'flex' }}><ShareIcon /></button>} />

      <div className="dt-score">
        <div className="top"><span>{/handball|básquet|basquet/i.test(liga) ? liga : `${liga} · ${esHb ? 'Handball' : 'Básquet'}`}</span>{enVivo ? <span className="lvb"><i />Live</span> : <span className="fin">Final</span>}</div>
        <div className="scr">
          <p className="tn">{nombres.away}</p>
          <p className="big">
            <span className={m.away != null && m.home != null && m.away < m.home ? 'lo' : ''}>{m.away ?? '–'}</span><i>–</i>
            <span className={m.away != null && m.home != null && m.home < m.away ? 'lo' : ''}>{m.home ?? '–'}</span>
          </p>
          <p className="tn">{nombres.home}</p>
        </div>
        <p className="v3clock">
          {enVivo ? <><b>{l.tiempo.split(' · ')[0]}</b>{l.tiempo.includes(' · ') ? ` · ${l.tiempo.split(' · ')[1]}${esHb ? '' : ' restantes'}` : ''}{ritmo ? ` · Ritmo ${Number(ritmo).toFixed(esHb ? 0 : 1)}` : ''}</>
            : <><b>Final</b>{sig?.total_final != null ? ` · Total ${sig.total_final}` : radar[0]?.total_final != null ? ` · Total ${radar[0].total_final}` : ''}</>}
        </p>
        {esHb && mit && (
          <div className="qrow" style={{ '--qn': 2 }}>
            <span /><span>1T</span><span>2T</span><span>T</span>
            {[['away', 0], ['home', 1]].map(([side, ix]) => {
              const h1 = String(mit.h1 || '').split('-')[ix], h2 = String(mit.h2 || '').split('-')[ix];
              return [<p className="nm" key={`n${side}`}>{abrev(nombres[side])}</p>, <b key={`a${side}`}>{h1 || '–'}</b>, <b key={`b${side}`} className={h2 ? '' : 'dim'}>{h2 || '–'}</b>, <b key={`t${side}`} className="tot">{m[side] ?? '–'}</b>];
            })}
          </div>
        )}
        {!esHb && qtr.length > 0 && (
          <div className="qrow" style={{ '--qn': qn }}>
            <span />{Array.from({ length: qn }, (_, i) => <span key={i}>{i < 4 ? `Q${i + 1}` : `OT${i - 3}`}</span>)}<span>T</span>
            <p className="nm">Pts</p>
            {Array.from({ length: qn }, (_, i) => { const q = qtr[i]; return <b key={i} className={q ? '' : 'dim'}>{q ? q.pts : '–'}</b>; })}
            <b className="tot">{m.total ?? pick?.total ?? '–'}</b>
          </div>
        )}
      </div>

      {sig ? (
        <div className="sigd">
          <div className="h"><span className="stt g"><i />Entrar</span><span className="lg">Emitida {ventanaDe(sig)}</span>{!Number.isNaN(edgeSig) && edgeSig != null && <span className="v3edge">Edge {fmtNum(edgeSig)}</span>}</div>
          <div className="grid">
            <div><p className="l">Pick</p><p className="v g">{pickOf(sig)}</p></div>
            <div><p className="l">Ventana</p><p className="v">{ventanaDe(sig).split(' · ')[0]}</p></div>
            <div><p className="l">Resultado</p><p className="v" style={resSig.cls === 'g' ? { color: 'var(--pos)' } : resSig.cls === 'neg' ? { color: 'var(--neg)' } : { color: 'var(--muted)', fontSize: 16 }}>{resSig.v}</p></div>
          </div>
          {why && <p className="why">{why}</p>}
        </div>
      ) : l ? (
        <div className="sigd">
          <div className="h"><span className={`stt ${STT_CLS[l.estado]}`}><i />{STT_TXT[l.estado]}</span><span className="lg">Ahora {l.tiempo}</span>{l.edge != null && <span className="v3edge">Edge {fmtNum(l.edge)}</span>}</div>
          {l.estado === 'VIGILAR' || l.estado === 'ENTRAR' ? (
            <div className="grid">
              <div><p className="l">{l.estado === 'ENTRAR' ? 'Pick' : 'Tendencia'}</p><p className={`v${l.estado === 'ENTRAR' ? ' g' : ''}`}>{l.estado === 'ENTRAR' ? pickLive(l) : (l.lado || '—')}</p></div>
              <div><p className="l">Umbral</p><p className="v">{fmtNum(l.umbral)}</p></div>
              <div><p className="l">Falta</p><p className={`v${l.falta > 0 ? ' wn' : ''}`}>{l.falta.toFixed(1)}</p></div>
            </div>
          ) : (
            <div className="grid">
              <div><p className="l">Proyección</p><p className="v">{l.proyeccion == null ? '—' : esHb ? l.proyeccion.toFixed(1) : Math.round(l.proyeccion)}</p></div>
              <div><p className="l">Línea O/U</p><p className="v">{l.linea ?? '—'}</p></div>
              <div><p className="l">Umbral</p><p className="v">{l.edge != null ? fmtNum(umbralDe(esHb, l.edge)) : esHb ? '+3 / −2' : '±10'}</p></div>
            </div>
          )}
          {why && <p className="why">{why}</p>}
        </div>
      ) : radar.length ? (
        <div className="sigd">
          <div className="h"><span className="sport">📡 RADAR</span><span className="lg">Ventana {radar[0].ventana}</span><span className="v3edge">Edge {fmtNum(radar[0].edge)}</span></div>
          <div className="grid">
            <div><p className="l">Pick</p><p className="v g">{radar[0].side} {radar[0].linea ?? ''}</p></div>
            <div><p className="l">Ventana</p><p className="v">{radar[0].ventana}</p></div>
            <div><p className="l">Resultado</p><p className="v" style={!radar[0].resolved ? { color: 'var(--muted)', fontSize: 16 } : radar[0].won ? { color: 'var(--pos)' } : { color: 'var(--neg)' }}>{!radar[0].resolved ? 'En juego' : radar[0].won ? 'WIN' : 'LOSS'}</p></div>
          </div>
        </div>
      ) : null}

      <EdgeChart pick={pick} esHb={esHb} lineaActual={l?.linea ?? sig?.line ?? null} proyeccion={l?.proyeccion ?? pick?.proyeccion ?? null} edgeActual={l?.edge ?? (model ? model.last.e : edgeSig)} />

      {eventos.length > 0 && (
        <div className="v3chart">
          <div className="h"><div><h3>Cronología</h3></div></div>
          <div className="timeline">
            {eventos.map((e) => <div className="tl" key={e.k}><p className="t">{e.lbl}</p><p className="b">{e.txt}</p></div>)}
          </div>
        </div>
      )}
    </div>
    {enVivo && (
      <div className="sticky-cta">
        <button type="button" className={`cta${siguiendo ? ' ghost' : ''}`} onClick={() => { toggle({ partido: partidoTxt }); toast(siguiendo ? 'Dejaste de seguir este partido' : 'Te avisamos cuando cierre', { ok: !siguiendo, ms: 2500 }); }}>
          {siguiendo ? <>Siguiendo este partido<small>· te avisamos al cerrar</small></> : <>Seguir este partido<small>· alerta al cerrar</small></>}
        </button>
      </div>
    )}
    </div>
  );
}
